/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, forwardRef, OnInit, Injector, input, signal, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, FormsModule } from '@angular/forms';
import { SelectOption } from '../input.models';
import { CustomErrorDisplayComponent } from '../custom-error-display/custom-error-display.component';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'lib-custom-radio-group',
  standalone: true,
  imports: [FormsModule, CustomErrorDisplayComponent, CommonModule, TranslateModule],
  templateUrl: './custom-radio-group.component.html',
  styles: [
    `
      .custom-tooltip-container {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .custom-tooltip-text {
        visibility: hidden;
        width: 160px;
        background-color: #e8f1f8; /* primary_light */
        color: #1c2229; /* basic */
        text-align: center;
        border-radius: 8px;
        padding: 8px;
        position: absolute;
        z-index: 50;
        bottom: 125%;
        left: 50%;
        margin-left: -80px;
        opacity: 0;
        transition: opacity 0.3s;
        box-shadow:
          0 4px 6px -1px rgb(0 0 0 / 0.1),
          0 2px 4px -2px rgb(0 0 0 / 0.1);
        font-size: 11px;
        font-weight: normal;
        pointer-events: none;
      }
      .custom-tooltip-text::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: #e8f1f8 transparent transparent transparent;
      }
      .custom-tooltip-container:hover .custom-tooltip-text {
        visibility: visible;
        opacity: 1;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomRadioGroupComponent),
      multi: true,
    },
  ],
})
export class CustomRadioGroupComponent implements ControlValueAccessor, OnInit {
  label = input<string>('');
  name = input<string>('');
  options = input<SelectOption[]>([]);
  layout = input<'vertical' | 'horizontal'>('vertical');
  errors = input<{ [key: string]: string }>({});

  readonly effectiveName = computed(() => {
    const currentName = this.name();
    if (currentName) return currentName;
    const currentLabel = this.label();
    if (currentLabel) {
      return (
        currentLabel.toLowerCase().replace(/\s+/g, '-') +
        '-' +
        Math.random().toString(36).substring(2, 7)
      );
    }
    return 'radio-group-' + Math.random().toString(36).substring(2, 7);
  });

  _value = signal<any>(undefined);
  isDisabled: boolean = false;
  ngControl: NgControl | null = null;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private _injector: Injector) {}

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    // Name generation logic is now handled by the 'effectiveName' computed signal.
  }

  writeValue(value: any): void {
    this._value.set(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  selectOption(value: any): void {
    if (!this.isDisabled) {
      this._value.set(value);
      this.onChange(this._value()); // Pass the signal's value
      this.onTouched(); // Typically, selecting a radio button also means it's touched
    }
  }
}
